import type { ReportMetaAggregateBy } from '@dashhudson/api-client/dashboard/model/raw';

import {
  DATE_RANGE_COMPARISON_PRESETS,
  DATE_RANGE_PRESETS,
} from '@dashhudson/dashing-ui/utils/date-ranges';
import 'dayjs/plugin/quarterOfYear';
import dayjs from 'dayjs';

import { REPORT_MENU } from '@/app/dashboards/reportConstants';
import { metrics as YOUTUBE_METRICS } from '@/app/youtube/constants';
import slUpgrade3 from '@/assets/images/featureUpgrade/sl-upgrade-3.png';
import { CHANNELS } from '@/models/dashboards/channels.enum';
import { GRAPH_SCALES } from '@/models/dashboards/graph-scales.enum';
import { CUSTOM_METRICS_PREFIX } from '@/models/dashboards/metrics.constants';
import { getEnumText, getEnumValue } from '@/models/enum.utils';
import { getResourceCenterArticleUrl } from '@/utils/resource-center';

export { CUSTOM_METRICS_PREFIX };

export const DASHBOARD_TOOLTIPS = {
  noBrandPermission: "Looks like this feature isn't included in your plan!",
  noAccessMessage: 'Contact your Customer Success Representative to try it out.',
  noIndustryBenchmarksData:
    'Benchmark data is unavailable prior to May 12, 2021. Please select a time period after this date.',
  noAccessToBrandTags: 'Contact your brand admin for permission to use brand tags',
  noAccessToCompetitorTags: 'Contact your brand admin for permission to use competitor tags',
  monetaryTooltipDisabled:
    'Metrics with monetary values are unavailable when ad accounts with different currencies are selected',
  estimatedMetricTooltipDisabled:
    'This metric is only available in Social Advertising Graph Reports',
  estimatedMetricTooltipDisabledForDisplay: 'This metric is not available for bar charts',
  customMetricIcon: 'This is a custom metric',
  tagBreakdownRequiredByCategory: {
    owned_report: 'Add brand tags to break down by tag',
    competitive_report:
      'Add brand tags or competitor tags to break down by tag. Individual brands or competitors are not supported.',
    earned_report: 'Add creator tags to break down by tag',
    default: 'Add brand tags, competitor tags, or creator tags to break down by tag',
  } as Record<string, string>,
  mediaTypeBreakdownUnsupportedForStories:
    'Media type breakdown is not supported when Instagram Stories is selected',
  contentTagBreakdownUnsupportedForMetric: 'Content tag breakdown is not supported for this metric',
  mediaTypeBreakdownUnsupportedForMetric: 'Media type breakdown is not supported for this metric',
  breakdownRequiresSingleBrand: 'This breakdown is only available when a single brand is selected',
  sentimentBreakdownUnsupportedForYouTube:
    'Sentiment breakdown is not available when YouTube is a selected channel',
  collaboratorBreakdownUnsupportedForNonCollab:
    'Collaborator breakdown is not available when only Non-Collab posts are selected',
};

export const DASHBOARD_COMMUNITY_MESSAGE_TYPES = {
  AD_COMMENTS: 'AD_COMMENTS',
  COMMENTS: 'COMMENTS',
  DMS: 'DMS',
  MENTIONS: 'MENTIONS',
  STORY_MENTION: 'STORY_MENTION',
};

export const MESSAGE_TYPE_CONSTANTS = {
  NON_SENTIMENT_MESSAGE_TYPES: [
    DASHBOARD_COMMUNITY_MESSAGE_TYPES.DMS,
    DASHBOARD_COMMUNITY_MESSAGE_TYPES.STORY_MENTION,
  ],
  SENTIMENT_SUPPORTED_MESSAGE_TYPES: [
    DASHBOARD_COMMUNITY_MESSAGE_TYPES.COMMENTS,
    DASHBOARD_COMMUNITY_MESSAGE_TYPES.AD_COMMENTS,
    DASHBOARD_COMMUNITY_MESSAGE_TYPES.MENTIONS,
  ],
};

export const FILTER_TOOLTIPS = {
  SENTIMENT_MESSAGE_TYPE_DISABLED:
    'The message types selected do not support sentiment. Only Comments, Ad Comments and Mentions support sentiment.',
  MESSAGE_TYPE_DISABLED: 'The message types selected do not support this filter.',
  NO_OPTIONS_AVAILABLE: (filterName: string): string =>
    `No ${filterName} available for your selected brands or channels`,
  YOUTUBE_SENTIMENT_DISABLED: 'Sentiment is not available when YouTube is a selected channel.',
  BRAND_CANT_ACCESS_SENTIMENT: (brandName: string): string =>
    `${brandName} does not have access to sentiment`,
  BRANDS_CANT_ACCESS_SENTIMENT: (brandNames: string): string =>
    `${brandNames} does not have access to sentiment`,
  DMS_TOOLTIP: 'Direct Messages includes both DMs and Story Mentions',
  STORY_MENTION_TOOLTIP: 'Story Mentions are included in Direct Messages',
};
const FORMAT_STRING = 'YYYY-MM-DD';

export const DASHBOARD_DRAWERS = {
  TEMPLATES: 'templates',
  REPORT_FORM_DRAWER: 'reportFormDrawer',
  AUTO_EXPORTS_DRAWER: 'autoExportsDrawer',
  NO_REPORT_FORM_DRAWER: 'noReportFormDrawer',
  NO_DASHBOARDS: 'noDashboards',
  SHARE_DASHBOARD: 'shareDashboard',
  ADD_SHAREABLE_LINK: 'addShareableLink',
  EDIT_SHAREABLE_LINK: 'editShareableLink',
  MEDIA_ITEM: 'mediaItem',
  DASHBOARD_ACTIONS: 'duplicateAndEdit',
  EDIT_REPORT_NAME_DRAWER: 'editReportNameDrawer',
};

export const AUTO_EXPORT_PANELS = {
  AUTO_EXPORT_LIST: 'autoExportList',
  AUTO_EXPORT_DETAIL: 'autoExportDetail',
};

export const REPORT_MODES = {
  ADD: {
    label: 'Add',
    buttonText: 'Add Report',
    value: 'ADD',
  },
  EDIT: {
    label: 'Edit',
    value: 'EDIT',
    buttonText: 'Update Report',
  },
  DUPLICATE: {
    label: 'Duplicate',
    value: 'DUPLICATE',
    buttonText: 'Duplicate Report',
  },
};

export const DASHBOARD_MODES = {
  CREATE: {
    LABEL: 'CREATE',
    BUTTON_TEXT: 'Add Report',
    VALUE: 'CREATE',
  },
  EDIT: {
    LABEL: 'Edit',
    BUTTON_TEXT: 'Save Dashboard',
    DISABLED_TOOLTIP_TEXT: "You haven't made any changes yet",
    TOAST_MESSAGE: 'Dashboard Edited',
    VALUE: 'EDIT',
  },
  DUPLICATE: {
    LABEL: 'Duplicate',
    BUTTON_TEXT: 'Create Dashboard',
    TOAST_MESSAGE: 'Dashboard Duplicated',
    VALUE: 'DUPLICATE',
  },
};

export const DASHBOARD_BANNER_VARIANTS = {
  PAGE: 'page',
  DRAWER: 'drawer',
};

export const DASHBOARD_USER_SEARCH_FIELDS = ['firstName', 'lastName', 'email'];

export const DASHBOARD_USER_ACTIONS = {
  REMOVE_USER: {
    label: 'Remove User',
    value: 'removeUser',
    icon: 'delete',
    class: 'text-danger  fill-icon-danger',
    disabled: false,
    tooltip: 'Revoke access to this dashboard',
  },
  TRANSFER_OWNERSHIP: {
    label: 'Transfer Ownership',
    value: 'transferOwnership',
    icon: 'user-plus',
    tooltip: 'Assign as the dashboard owner with full admin privileges',
  },
};

export const DASHBOARD_BRAND_ACTIONS = {
  REMOVE_BRAND: {
    label: 'Remove Access',
    value: 'removeBrand',
    icon: 'delete',
    class: 'text-danger  fill-icon-danger',
    disabled: false,
    tooltip: 'Revoke this brand’s access to this dashboard',
  },
};

export const DASHBOARD_BRAND_ACTIONS_TOASTS = {
  SHARE_SUCCESS: ({ brand, dashboard }: { brand: string; dashboard: string }): string =>
    `${brand} has been added to ${dashboard}.`,
  SHARE_ERROR: ({ brand, dashboard }: { brand: string; dashboard: string }): string =>
    `Failed to add ${brand} to ${dashboard}.`,
  UPDATE_SUCCESS: ({ brand, dashboard }: { brand: string; dashboard: string }): string =>
    `${brand}’s role has been updated on ${dashboard}.`,
  UPDATE_ERROR: ({ brand, dashboard }: { brand: string; dashboard: string }): string =>
    `Failed to update ${brand}’s role on ${dashboard}.`,
  REMOVE_SUCCESS: ({ brand, dashboard }: { brand: string; dashboard: string }): string =>
    `${brand} has been removed from ${dashboard}.`,
  REMOVE_ERROR: ({ brand, dashboard }: { brand: string; dashboard: string }): string =>
    `Failed to remove ${brand} from ${dashboard}.`,
};

export const DASHBOARD_USER_ACTIONS_TOASTS = {
  REMOVE_USER_SUCCESS: ({ user, dashboard }: { user: string; dashboard: string }): string =>
    `${user} has been removed from ${dashboard}.`,
  REMOVE_USER_ERROR: ({ user, dashboard }: { user: string; dashboard: string }): string =>
    `Failed to remove ${user} from ${dashboard}.`,
  TRANSFER_OWNERSHIP_SUCCESS: ({ user, dashboard }: { user: string; dashboard: string }): string =>
    `${user} is now the owner of ${dashboard}.`,
  TRANSFER_OWNERSHIP_ERROR: ({ user, dashboard }: { user: string; dashboard: string }): string =>
    `Failed to transfer ownership of ${dashboard} to ${user}.`,
};

export const CONTENT_TAG_DISABLED_TOOLTIP_TEXT =
  'Select a brand first to access the Content Tags option';

export const ADD_DASHBOARD_FORM_OPTIONS = {
  COMPETITORS: {
    hint: 'Select one or multiple competitors',
  },
  CREATORS: {
    label: 'Creators',
    hint: 'Select creators to include in your dashboard. This is optional.',
    placeholder: 'Select creators (optional)',
  },
};

export const INITIAL_TEMPLATE_FORM_STATE = {
  name: null,
  brands: null,
  competitors: null,
  creatorIds: null,
  contentTagIds: null,
  messageTagIds: null,
  topicIds: [],
  comparisonTopicIds: [],
  topicValidation: null, // Combined validation field for topics and comparison topics
  channels: null,
};

export const DASHBOARD_TEMPLATE_OPTIONS = {
  BRANDS: {
    value: 'BRANDS',
    formKey: 'brands',
  },
  COMPETITORS: {
    value: 'COMPETITORS',
    formKey: 'competitors',
  },
  CREATORS: {
    value: 'CREATORS',
    formKey: 'creatorIds',
  },
  CONTENT_TAG_IDS: {
    value: 'CONTENT_TAG_IDS',
    formKey: 'contentTagIds',
  },
  MESSAGE_TAG_IDS: {
    value: 'MESSAGE_TAG_IDS',
    formKey: 'messageTagIds',
  },
  TOPIC_IDS: {
    value: 'TOPIC_IDS',
    formKey: 'topicIds',
  },
  COMPARISON_TOPIC_IDS: {
    value: 'COMPARISON_TOPIC_IDS',
    formKey: 'comparisonTopicIds',
  },
  CHANNELS: {
    value: 'CHANNELS',
    formKey: 'channels',
  },
};

export const DASHBOARD_TEMPLATE_FORM_OPTIONS = {
  [DASHBOARD_TEMPLATE_OPTIONS.CONTENT_TAG_IDS.value]: {
    name: DASHBOARD_TEMPLATE_OPTIONS.CONTENT_TAG_IDS.formKey,
    label: 'Content Tags',
    hint: 'All tags used on this reports will be replaced with the tags selected above',
  },
  [DASHBOARD_TEMPLATE_OPTIONS.MESSAGE_TAG_IDS.value]: {
    name: DASHBOARD_TEMPLATE_OPTIONS.MESSAGE_TAG_IDS.formKey,
    label: 'Message Tags',
  },
  [DASHBOARD_TEMPLATE_OPTIONS.TOPIC_IDS.value]: {
    name: DASHBOARD_TEMPLATE_OPTIONS.TOPIC_IDS.formKey,
    label: 'Topics',
    hint: 'Select topics to include in your dashboard',
  },
  [DASHBOARD_TEMPLATE_OPTIONS.COMPARISON_TOPIC_IDS.value]: {
    name: DASHBOARD_TEMPLATE_OPTIONS.COMPARISON_TOPIC_IDS.formKey,
    label: 'Comparison Topics',
    hint: 'Select comparison topics to include in your dashboard',
  },
  [DASHBOARD_TEMPLATE_OPTIONS.CHANNELS.value]: {
    name: DASHBOARD_TEMPLATE_OPTIONS.CHANNELS.formKey,
    label: 'Channels',
    hint: 'Select channels to filter your topics',
  },
};

export const DASHBOARD_TEMPLATE_INPUT_SELECT_PROPS = {
  [DASHBOARD_TEMPLATE_OPTIONS.CONTENT_TAG_IDS.value]: {
    hideControls: false,
    manageButtonLabel: 'Manage Content Tags',
    multiple: true,
    placeholder: 'Add content tags',
    searchable: true,
  },
  [DASHBOARD_TEMPLATE_OPTIONS.MESSAGE_TAG_IDS.value]: {
    hideControls: false,
    multiple: true,
    placeholder: 'Add Message Tags',
    searchable: true,
    disableSelectAll: true,
    disableBranchSelectAll: false,
  },
  [DASHBOARD_TEMPLATE_OPTIONS.TOPIC_IDS.value]: {
    hideControls: false,
    manageButtonLabel: 'Manage Topics',
    multiple: true,
    placeholder: 'Search and select topics',
    searchable: true,
    disableSelectAll: true,
  },
  [DASHBOARD_TEMPLATE_OPTIONS.COMPARISON_TOPIC_IDS.value]: {
    hideControls: false,
    manageButtonLabel: 'Manage Comparison Topics',
    multiple: true,
    placeholder: 'Search and select comparison topics',
    searchable: true,
    disableSelectAll: true,
  },
  [DASHBOARD_TEMPLATE_OPTIONS.CHANNELS.value]: {
    hideControls: false,
    multiple: true,
    placeholder: 'Select channels',
    searchable: false,
    disableSelectAll: false,
    forceSelectAll: true,
    chipSelector: true,
  },
};

export const DUPLICATE_DASHBOARD_FORM_OPTIONS = {
  DASHBOARD_NAME: {
    formLabel: 'Dashboard Name',
    placeholder: 'Name your dashboard',
  },
  ADVANCED_SETTINGS_SECTION: {
    label: 'Advanced Settings',
  },
  REPLACE_BRANDS: {
    formLabel: 'Replace Brands with',
    hint: 'All reports in this Dashboard will update to reflect the selected brand',
    placeholder: 'Select brands',
  },
  REPLACE_COMPETITORS: {
    formLabel: 'Replace your Competitors',
    hint: 'All competitive reports in this Dashboard will update to reflect the selected competitors',
    placeholder: 'Select competitors',
  },
  REPLACE_CONTENT_TAGS: {
    formLabel: 'Content Tags',
    hint: 'All tags used on this reports will be replaced with the tags selected above',
    placeholder: 'Select content tags',
    manageButtonLabel: 'Manage Content Tags',
  },
};

export const EDIT_DASHBOARD_FORM_OPTIONS = {
  DASHBOARD_NAME: {
    formLabel: 'Dashboard Name',
    placeholder: 'Name your dashboard',
  },
  REPLACE_BRANDS: {
    formLabel: 'Replace Brands with',
    hint: 'All reports in this Dashboard will update to reflect the selected brand',
    placeholder: 'Select brands',
  },
  REPLACE_COMPETITORS: {
    formLabel: 'Replace your Competitors',
    hint: 'All competitive reports in this Dashboard will update to reflect the selected competitors',
    placeholder: 'Select competitors',
  },
  REPLACE_CREATORS: {
    formLabel: 'Replace your Creators',
    hint: 'All creator reports in this Dashboard will update to reflect the selected creators',
    placeholder: 'Select creators',
  },
  SAVE_BUTTON: {
    label: 'Save Changes',
  },
  CONTENT_BANNER_MESSAGE: {
    value: 'You are editing an existing dashboard',
  },
};

export const SHARE_DASHBOARD_CONFIRMATION_DIALOGS = {
  REMOVE_USER: {
    group: 'global',
    acceptLabel: 'Remove User',
    acceptSeverity: 'danger',
    message: 'This member will lose access to this dashboard.',
  },
  // Removing the direct grant does not revoke access inherited from a brand, so promising they
  // "will lose access" would be a lie.
  REMOVE_USER_WITH_BRAND_ACCESS: {
    group: 'global',
    acceptLabel: 'Remove User',
    acceptSeverity: 'danger',
    message: ({ brand }: { brand: string }): string =>
      `This member will keep access to this dashboard through ${brand}.`,
  },
  REMOVE_BRAND: {
    group: 'global',
    acceptLabel: 'Remove Access',
    acceptSeverity: 'danger',
    message: 'Brand users will lose access to this dashboard.',
  },
  TRANSFER_OWNERSHIP_DIALOG: {
    group: 'global',
    acceptLabel: 'Transfer Ownership',
    acceptSeverity: 'danger',
    message: 'They will become the owner of this dashboard and can revoke your access.',
  },
};

export const DASHBOARD_ACTION_BANNER_PROPS = Object.freeze({
  templates: {
    title: 'Create Your First Dashboard',
    drawerHeaderTitle: 'Create a Dashboard',
    ctaButtonLabel: 'Create Dashboard',
    ctaButtonTooltip: 'You must fill in the mandatory sections',
  },
  reports: {
    noReportsTitle: 'Add Reports to your Dashboard',
    drawerHeaderTitle: 'Add Report',
    ctaButtonLabel: 'Create Report',
    drawerTitle: 'Add Report',
  },
});

const GRAPH_SCALE_LOCAL_STORE_KEY = 'dashboards_graph_scale';
const CONTENT_TAG_IDS_LOCAL_STORE_KEY = 'dashboards_content_tag_ids';
const CONTENT_TAG_MATCH_TYPE_LOCAL_STORE_KEY = 'dashboards_content_tag_match_type';
const MESSAGE_TAG_IDS_LOCAL_STORE_KEY = 'dashboards_message_tag_ids';
const MESSAGE_TAG_MATCH_TYPE_LOCAL_STORE_KEY = 'dashboards_message_tag_match_type';
const BRAND_IDS_LOCAL_STORE_KEY = 'dashboards_brand_ids';
const BRAND_TAG_IDS_LOCAL_STORE_KEY = 'dashboards_brand_tag_ids';
const COMPETITOR_IDS_LOCAL_STORE_KEY = 'dashboards_competitor_ids';
const COMPETITOR_TAG_IDS_LOCAL_STORE_KEY = 'dashboards_competitor_tag_ids';
const CAMPAIGN_IDS_LOCAL_STORE_KEY = 'dashboards_campaign_ids';
const BOARD_IDS_LOCAL_STORE_KEY = 'dashboards_board_ids';
const AUTHORSHIP_LOCAL_STORE_KEY = 'dashboards_authorship';
const COLLABORATOR_USERNAMES_LOCAL_STORE_KEY = 'dashboards_collaborator_usernames';
const LAYOUT_LOCAL_STORE_KEY = 'dashboards_layout';

const NUMBER_OF_DASHBOARD_COLS = 6;
const DASHBOARD_ROW_HEIGHT_IN_PIXELS = 25;

const MAX_GRAPH_DATASETS = 10;

const MAX_NUMBER_OF_CONTENT_REPORTS = 10;

export const SEARCH_DEBOUNCE_TIME = 250;

export const MAX_ADS_TABLE_REPORT_ROWS = 50;

const REPORT_DATE_RANGE_LS_KEY = 'reportDateRange';
const CONTEXT_DATE_RANGE_LS_KEY = 'contextDateRange';
const CONTEXT_PERIOD_MODE_LS_KEY = 'dashboards_context_period_mode';
const REPORTING_PERIOD_MODE_LS_KEY = 'dashboards_reporting_period_mode';

const ROLES = Object.freeze({
  EDITOR: {
    value: 'EDITOR',
    icon: 'edit',
    tooltipText: 'Can manage reports, users and permissions.',
  },
  VIEWER: {
    value: 'VIEWER',
    icon: 'eye',
    tooltipText: 'Can view reports and change reporting periods.',
  },
  OWNER: {
    value: 'OWNER',
  },
});

export const SHARE_DASHBOARD_DRAWER = {
  title: 'Share Dashboard',
  description: 'Sharing a dashboard gives the recipient access to it in their Dash Social account',
  searchPlaceholder: 'Search Users',
  doneButtonLabel: 'Done',
};

export const SHARE_DASHBOARD_TEAM_TABS = {
  USERS: {
    title: 'Users',
    value: 'users',
    tooltipIcon: 'Individual and brand access combine. The highest access level applies.',
  },
  BRANDS: {
    title: 'Brands',
    value: 'brands',
    tooltipIcon: 'Brand access applies to everyone in the brand, including people added later.',
  },
} as const;

export const SHARE_DASHBOARD_BRANDS_SECTION = {
  searchPlaceholder: 'Search Brands',
  inaccessibleBrandTooltip:
    'This brand has access to the dashboard but you don’t have access to the brand',
  viaBrandLabel: (brandName: string): string => `via ${brandName}`,
  viaBrandTooltip: (brandNames: string[]): string =>
    `Has access because ${brandNames.join(', ')} ${brandNames.length === 1 ? 'was' : 'were'} ` +
    'shared this dashboard',
};

export const AUTO_EXPORT_DRAWER = {
  title: 'Schedule Email Delivery',
  FORM_PROPS: {
    name: {
      name: 'name',
      label: 'Schedule Name',
      placeHolder: 'Name this particular email delivery schedule',
      hint: 'This field is for internal organizational purposes and will not be visible to the email recipients',
    },
    frequency: {
      name: 'frequency',
      label: 'Frequency',
    },
    time_of_day: {
      name: 'time_of_day',
      label: 'Time',
    },
    day_of_week: {
      name: 'day_of_week',
      label: 'Day',
    },
  },
};

export const OVERVIEW_EVENTS = {
  SORT: 'Sortable - Select',
  SEARCH: 'Dashboard Overview - Search',
  VIEW: 'Dashboard Overview - View',
  VISION_BANNER_CLICK: 'Dashboard Overview - Vision Banner Click',
};

export const DASHBOARD_OVERVIEW_HEADER = {
  TITLE: 'Dashboards',
  SEARCH_PLACEHOLDER: 'Search',
  CREATE_DASHBOARD_BUTTON_TEXT: 'Create Dashboard',
  CURRENT_VIEW: {
    GRID: 'Cards',
    TABLE: 'Table',
  },
};

export const DASHBOARD_OVERVIEW_SORT_BY = {
  CREATED_AT: {
    ASC: 'CREATED_AT',
    DESC: '-CREATED_AT',
  },
  NAME: {
    ASC: 'NAME',
    DESC: '-NAME',
  },
};

export const OVERVIEW_SORT_DEFAULT_SELECTION = {
  sortOrder: 'DESC',
  selected: { value: 'CREATED_AT', label: 'Date Added' },
};

export const DASHBOARD_OVERVIEW_SORT_OPTIONS = {
  CREATED_AT: { value: 'CREATED_AT', label: 'Date Added' },
  NAME: { value: 'NAME', label: 'Dashboard Name' },
};

export const DASHBOARD_OVERVIEW_FIELD_DATA = {
  NAME: { field: 'name', label: 'Name' },
  REPORT_COUNT: { field: 'report_count', label: 'Reports' },
  BRAND_COUNT: { field: 'brand_count', label: 'Brands' },
  COMPETITOR_COUNT: { field: 'competitor_count', label: 'Competitors' },
  CREATED_AT: { field: 'created_at', label: 'Date Added' },
  CHANNELS: { field: 'channels', label: 'Channels' },
  OWNER_NAME: { field: 'owner_name', label: 'Owner' },
};

const CONTENT_TYPE_OPTIONS = Object.freeze({
  CONTENT_OWNED: {
    value: 'CONTENT_OWNED',
    text: 'Owned',
  },
  CONTENT_UGC: {
    value: 'CONTENT_UGC',
    text: 'User Generated',
  },
});

const IG_CONTENT_OPTIONS = Object.freeze({
  OWNED: {
    value: 'OWNED',
    text: 'Owned',
  },
  UGC: {
    value: 'UGC',
    text: 'User Generated',
  },
});

const getRoleProp = (channel: unknown, property: string): unknown => {
  const key = getEnumValue(channel) as keyof typeof ROLES;
  const config = ROLES[key];
  return config?.[property as keyof typeof config];
};

export function getRoleText(value: unknown): string {
  const text = getRoleProp(value, 'text') as string | undefined;
  return text || getEnumText(value);
}

const USER_STATUS = Object.freeze({
  ACTIVE: 0,
  INACTIVE: 1,
});

const POPUP_MESSAGES = {
  leaveDashboard: {
    title: 'Are you sure you want to leave?',
    message: 'You will no longer have access to this Dashboard.',
    confirmAlias: 'Leave Dashboard',
  },
};

export const ERROR_MESSAGES = {
  savingAutoExport: {
    serverError: 'An error occurred while attempting to save your scheduled delivery.',
  },
};

export const AUTO_EXPORT_FORMATS = Object.freeze({
  SPREADSHEET: {
    value: 'SPREADSHEET',
    text: 'XLSX',
    label: 'XLSX',
  },
  PDF: {
    value: 'PDF',
    text: 'PDF',
    label: 'PDF',
  },
});

export const AUTO_EXPORT_FREQUENCIES = Object.freeze({
  DAILY: {
    value: 'DAILY',
    text: 'Daily',
    label: 'Daily',
  },
  WEEKLY: {
    value: 'WEEKLY',
    text: 'Weekly',
    label: 'Weekly',
  },
  MONTHLY: {
    value: 'MONTHLY',
    text: 'Monthly',
    label: 'Monthly',
  },
});

export const AUTO_EXPORT_DAY_OF_WEEK = Object.freeze({
  SUNDAY: {
    value: 'SUNDAY',
    text: 'Sunday',
    label: 'Sunday',
    weeklyExportEndDay: 'Saturday',
  },
  MONDAY: {
    value: 'MONDAY',
    text: 'Monday',
    label: 'Monday',
    weeklyExportEndDay: 'Sunday',
  },
  TUESDAY: {
    value: 'TUESDAY',
    text: 'Tuesday',
    label: 'Tuesday',
    weeklyExportEndDay: 'Monday',
  },
  WEDNESDAY: {
    value: 'WEDNESDAY',
    text: 'Wednesday',
    label: 'Wednesday',
    weeklyExportEndDay: 'Tuesday',
  },
  THURSDAY: {
    value: 'THURSDAY',
    text: 'Thursday',
    label: 'Thursday',
    weeklyExportEndDay: 'Wednesday',
  },
  FRIDAY: {
    value: 'FRIDAY',
    text: 'Friday',
    label: 'Friday',
    weeklyExportEndDay: 'Thursday',
  },
  SATURDAY: {
    value: 'SATURDAY',
    text: 'Saturday',
    label: 'Saturday',
    weeklyExportEndDay: 'Friday',
  },
});

export const DB_MODE = {
  CREATE: 'CREATE',
  RENAME: 'RENAME',
  DUPLICATE: 'DUPLICATE',
  EDIT: 'EDIT',
};

export const COMPETITIVE_AVERAGE_KEY = 'competitor_average';
export const COMPETITIVE_AVERAGE_NAME = 'Competitor Average';

export const DATA_TYPES = Object.freeze({
  ACCOUNT_TAG: 'ACCOUNT_TAG',
  BRAND: 'BRAND',
  CHANNEL: 'CHANNEL',
  COMPETITOR: 'COMPETITOR',
  COMPETITOR_AVERAGE: 'COMPETITOR_AVERAGE',
  BENCHMARK: 'BENCHMARK',
  CONTENT_TAG: 'CONTENT_TAG',
  PUBLISHER_PLATFORM: 'publisher_platform',
  COUNTRY: 'country',
  REGION: 'region',
  AGE_GENDER: 'age_gender',
  TAG: 'TAG',
  CREATOR: 'CREATOR',
  SENTIMENT: 'SENTIMENT',
  MESSAGE_TYPE: 'MESSAGE_TYPE',
  ASSIGNEE: 'ASSIGNEE',
  FOLLOWER_STATUS: 'FOLLOWER_STATUS',
  COLLABORATOR: 'COLLABORATOR',
  SOURCE_CAMPAIGN: 'SOURCE_CAMPAIGN',
});

export const TAG_BASED_DATA_TYPES = Object.freeze([DATA_TYPES.TAG, DATA_TYPES.ACCOUNT_TAG]);

export const AVATAR_PLACEHOLDER_DATA_TYPES = Object.freeze([
  DATA_TYPES.BRAND,
  DATA_TYPES.COMPETITOR,
  DATA_TYPES.CREATOR,
  DATA_TYPES.COLLABORATOR,
]);

export const LISTENING_DATA_TYPES = Object.freeze({
  AGE: 'AGE',
  CHANNEL: 'CHANNEL',
  CITY: 'CITY',
  COUNTRY: 'COUNTRY',
  GENDER: 'GENDER',
  INDUSTRY: 'INDUSTRY',
  MEDIA_TYPE: 'MEDIA_TYPE',
  MENTION_TYPE: 'MENTION_TYPE',
  SENTIMENT: 'SENTIMENT',
  STATE_PROVINCE: 'STATE_PROVINCE',
  MESSAGE_TAG: 'MESSAGE_TAG',
  TOPIC: 'TOPIC',
});

export const ALL_DATA_TYPES = Object.freeze({
  ...DATA_TYPES,
  ...LISTENING_DATA_TYPES,
});

type AggregateByValue = (typeof ALL_DATA_TYPES)[keyof typeof ALL_DATA_TYPES];

// No report form aggregates by these yet.
type UnmodelledAggregateBy =
  | 'AD'
  | 'AD_SET'
  | 'BOARD'
  | 'CAMPAIGN'
  | 'GALLERY'
  | 'LANDING_PAGE'
  | 'PLACEMENT'
  | 'TOTAL';

// Declared lowercase here, uppercase in the spec; unverified which the endpoint accepts.
type CaseDivergentAggregateBy = 'AGE_GENDER' | 'PUBLISHER_PLATFORM' | 'REGION';

// Series types name a drawn line and are never sent as aggregate_by.
type SeriesDataType = 'BENCHMARK' | 'COMPETITOR' | 'COMPETITOR_AVERAGE' | 'CREATOR';

type AssertNever<T extends never> = T;

// A new aggregate_by in api-client lands here as a type error naming it.
/** @public */
export type UnaccountedAggregateBy = AssertNever<
  Exclude<
    ReportMetaAggregateBy,
    AggregateByValue | UnmodelledAggregateBy | CaseDivergentAggregateBy
  >
>;

// The mirror: a value we declare that api-client does not accept, or no longer does.
/** @public */
export type UnacceptedDataType = AssertNever<
  Exclude<
    AggregateByValue,
    ReportMetaAggregateBy | SeriesDataType | Lowercase<CaseDivergentAggregateBy>
  >
>;

export const sentimentColorMap = Object.freeze({
  POSITIVE: '#24A677',
  NEGATIVE: '#F46565',
  NEUTRAL: '#4299E9',
});

export const TOTAL_METRIC_GROUPS = Object.freeze({
  ACCOUNT_TAG: 'ACCOUNT_TAG',
  CHANNEL: 'CHANNEL',
  BRAND: 'BRAND',
  TOTAL: 'TOTAL',
  TAG: 'TAG',
  SOURCE_CAMPAIGN: 'SOURCE_CAMPAIGN',
  REGION: 'REGION',
  COUNTRY: 'COUNTRY',
  PUBLISHER_PLATFORM: 'PUBLISHER_PLATFORM',
  AGE_GENDER: 'AGE_GENDER',
  MEDIA_TYPE: 'MEDIA_TYPE',
  CREATOR: 'CREATOR',
});

export const TAG_BASED_AGGREGATE_TYPES = Object.freeze([
  TOTAL_METRIC_GROUPS.TAG,
  TOTAL_METRIC_GROUPS.ACCOUNT_TAG,
]);

export const CHART_DATA_LIMIT = 12;

export const OTHER_SLICE_LABEL = 'Other';

export const NO_ACCESS_MESSAGES = Object.freeze({
  average: 'Average not available.',
  brand: "You don't have access to this brand.",
  channel: "You don't have access to this channel.",
  metric: "You don't have access to this metric.",
  disconnect: 'Account Disconnected.',
  feature: "You don't have access to this feature.",
  competitor: "You don't have access to this competitor.",
  noAccess: "You Don't Have Access",
  sort_by: "Sort by 'Day'",
  benchmark: "This brand doesn't have access to competitive insights",
  dashboards: "This brand doesn't have access to Dashboards",
  adsBrand: "This brand doesn't have access to ad analytics",
  adsUser: "You don't have access to ad analytics for this brand",
});

const instagramContentReportMediaMetrics = Object.freeze({
  labels: {
    comments_count: 'Comments',
    engagement: 'Engagement Rate',
    like_count: 'Likes',
    saved: 'Saves',
    total_engagement: 'Engagements',
    reach: 'Estimated Reach',
    effectiveness_engagements: 'Effectiveness',
  },
  metrics: ['ENGAGEMENT_RATE', 'EFFECTIVENESS_ENGAGEMENTS', 'TOTAL_ENGAGEMENTS'],
  collabMetrics: ['ORGANIC_LIKES', 'ORGANIC_COMMENTS', 'SAVES', 'ENGAGEMENT_RATE'],
});

const instagramUgcContentReportMediaMetrics = Object.freeze({
  labels: {
    engagement_rate_public: 'Engagement Rate - Followers',
    effectiveness: 'Effectiveness',
    total_engagements: 'Engagements',
    total_comments: 'Comments - Total',
  },
  metrics: [
    'UGC_ENGAGEMENT_RATE',
    'UGC_EFFECTIVENESS',
    'UGC_TOTAL_ENGAGEMENTS',
    'UGC_ORGANIC_COMMENTS',
  ],
});

const instagramStoriesContentReportMediaMetrics = Object.freeze({
  labels: {
    ig_story_avg_reach: 'Avg. Reach',
    ig_story_avg_views: 'Avg. Views',
    ig_story_avg_completion_rate_v2: 'Avg. Completion Rate',
    ig_story_avg_exit_rate_views: 'Avg. Exit Rate',
    ig_story_swipe_ups: 'Stories Link Clicks',
    reach: 'Reach',
    views_v2: 'Views',
    completion_rate: 'Completion Rate',
    exit_rate_views: 'Exit Rate',
  },
  metrics: ['AVG_REACH', 'AVG_VIEWS', 'AVG_COMPLETION_RATE_VIEWS', 'AVG_EXIT_RATE_VIEWS'],
  storyFrameMetrics: [
    'STORY_FRAMES_REACH',
    'STORY_FRAMES_VIEWS',
    'STORY_FRAMES_COMPLETION_RATE_VIEWS',
    'STORY_FRAMES_EXIT_RATE_VIEWS',
  ],
});

const instagramStoriesUgcContentReportMediaMetrics = Object.freeze({
  labels: {
    reach: 'Reach',
    views_v2: 'Views',
    completion_rate_views: 'Completion Rate',
    emv_views: 'EMV',
  },
  metrics: ['REACH', 'VIEWS', 'COMPLETION_RATE', 'EMV'],
});

const instagramCompetitiveContentReportMediaMetrics = Object.freeze({
  labels: {
    commentsCount: 'Comments',
    engagement: 'Engagement Rate',
    likeCount: 'Likes',
    reach: 'Estimated Reach',
  },
  metrics: ['ORGANIC_COMMENTS', 'ENGAGEMENT_RATE', 'REACH', 'ORGANIC_LIKES'],
});

const facebookContentReportMediaMetrics = Object.freeze({
  metrics: ['ENGAGEMENT_RATE', 'IMPRESSIONS_BY_POST', 'TOTAL_ENGAGEMENTS'],
});

const facebookCompetitiveContentReportMediaMetrics = Object.freeze({
  labels: {
    comments: 'Comments',
    engagementRatePublic: 'Engagement Rate',
    reactions: 'Reactions',
    shares: 'Shares',
  },
  metrics: ['COMMENTS', 'ENGAGEMENT_RATE_PUBLIC', 'REACTIONS', 'SHARES'],
});

const linkedinContentReportMediaMetrics = Object.freeze({
  metrics: ['ENGAGEMENTS_BY_POST', 'ENGAGEMENT_RATE', 'IMPRESSIONS', 'CLICK_THROUGH_RATE_BY_POST'],
});

const pinterestContentReportMediaMetrics = Object.freeze({
  labels: {
    total_closeups: 'Pin Clicks',
    engagement_rate: 'Engagement Rate',
    total_impressions: 'Impressions',
    total_clicks: 'Outbound Clicks',
    total_saves: 'Saves',
    video_views: 'Video Views',
    average_watch_time: 'Average Watch Time',
  },
  metrics: ['ENGAGEMENT_RATE', 'SAVES', 'IMPRESSIONS', 'TOTAL_VIDEO_VIEWS'],
});

const twitterContentReportMediaMetrics = Object.freeze({
  labels: {
    engagements: 'Total Engagements',
    url_clicks: 'Link Clicks',
    follows: 'User Follows',
    user_profile_clicks: 'Profile Clicks',
  },
  metrics: ['ENGAGEMENT_RATE', 'TOTAL_ENGAGEMENTS', 'IMPRESSIONS'],
});

const twitterCompetitiveContentReportMediaMetrics = Object.freeze({
  labels: {
    engagementsTotal: 'Engagements',
    likes: 'Likes',
    videoViews: 'Video Views',
    retweets: 'Reposts',
  },
  metrics: ['ENGAGEMENTS', 'LIKES', 'VIDEO_VIEWS', 'REPOSTS'],
});

const tiktokContentReportMediaMetrics = Object.freeze({
  labels: {
    entertainment_score: 'Entertainment Score',
    views: 'Video Views',
    total_engagements: 'Total Engagements',
    average_time_watched: 'Avg. Time Watched',
  },
  metrics: ['ENTERTAINMENT_SCORE', 'TOTAL_VIDEO_VIEWS', 'TOTAL_ENGAGEMENTS'],
});

const tiktokCompetitiveContentReportMediaMetrics = Object.freeze({
  metrics: ['ENGAGEMENTS', 'VIDEO_VIEWS', 'LIKES', 'COMMENTS'],
});

const tiktokUgcContentReportMediaMetrics = Object.freeze({
  labels: {
    views: 'Video Views',
    likes: 'Likes',
    comments: 'Comments',
    emv: 'EMV',
  },
  metrics: ['UGC_VIDEO_VIEWS', 'UGC_LIKES', 'UGC_COMMENTS', 'UGC_EMV'],
});

const youtubeContentReportMediaMetrics = Object.freeze({
  metrics: ['TOTAL_VIDEO_VIEWS', 'TOTAL_AVG_VIEW_DURATION', 'TOTAL_ENGAGEMENTS'],
  labels: {
    [YOUTUBE_METRICS.VIEWS.field]: 'Video Views',
    [YOUTUBE_METRICS.AVG_VIEW_DURATION.field]: 'Avg. View Duration',
    [YOUTUBE_METRICS.TOTAL_ENGAGEMENTS.field]: 'Total Engagements',
    [YOUTUBE_METRICS.AVG_VIEW_PERCENTAGE.field]: 'Avg. % Viewed',
  },
});

const youtubeCompetitiveContentReportMediaMetrics = Object.freeze({
  labels: {
    [YOUTUBE_METRICS.COMMENTS.field]: YOUTUBE_METRICS.COMMENTS.label,
    [YOUTUBE_METRICS.LIKES.field]: YOUTUBE_METRICS.LIKES.label,
    [YOUTUBE_METRICS.VIEWS.field]: YOUTUBE_METRICS.VIEWS.label,
  },
  metrics: ['COMMENTS', 'LIKES', 'VIDEO_VIEWS'],
});

export const CONTENT_REPORT_DEFAULT_MEDIA_METRICS = Object.freeze({
  [CHANNELS.INSTAGRAM.value]: instagramContentReportMediaMetrics,
  [CHANNELS.INSTAGRAM_UGC.value]: instagramUgcContentReportMediaMetrics,
  [CHANNELS.INSTAGRAM_STORIES.value]: instagramStoriesContentReportMediaMetrics,
  [CHANNELS.INSTAGRAM_STORIES_UGC.value]: instagramStoriesUgcContentReportMediaMetrics,
  [CHANNELS.INSTAGRAM_COMPETITIVE.value]: instagramCompetitiveContentReportMediaMetrics,
  [CHANNELS.LINKEDIN.value]: linkedinContentReportMediaMetrics,
  [CHANNELS.FACEBOOK.value]: facebookContentReportMediaMetrics,
  [CHANNELS.FACEBOOK_COMPETITIVE.value]: facebookCompetitiveContentReportMediaMetrics,
  [CHANNELS.PINTEREST.value]: pinterestContentReportMediaMetrics,
  [CHANNELS.TWITTER.value]: twitterContentReportMediaMetrics,
  [CHANNELS.TWITTER_COMPETITIVE.value]: twitterCompetitiveContentReportMediaMetrics,
  [CHANNELS.TIKTOK.value]: tiktokContentReportMediaMetrics,
  [CHANNELS.TIKTOK_COMPETITIVE.value]: tiktokCompetitiveContentReportMediaMetrics,
  [CHANNELS.TIKTOK_UGC.value]: tiktokUgcContentReportMediaMetrics,
  [CHANNELS.YOUTUBE.value]: youtubeContentReportMediaMetrics,
  [CHANNELS.YOUTUBE_COMPETITIVE.value]: youtubeCompetitiveContentReportMediaMetrics,
});

export const DASHBOARD_COMPETITIVE_RADIO_OPTIONS = Object.freeze({
  Competitors: {
    label: 'Competitors',
    value: 'Competitors',
  },
  Tags: { label: 'Tags', value: 'Tags' },
});

export const DASHBOARD_OWNED_RADIO_OPTIONS = Object.freeze({
  Brands: {
    label: 'Brands',
    value: 'Brands',
  },
  Tags: { label: 'Tags', value: 'Tags' },
});

export const DASHBOARD_EVENT_NAMES = {
  UPGRADE_NOW_CLICKED: 'Upgrade Options - Button Clicked',
  MANAGE_TAG_BUTTON_CLICKED: 'Manage Tags',
  MANGE_CUSTOM_METRIC_BUTTON_CLICKED: 'Manage Custom Metrics',
};

export const REPORT_CSV_LAYOUTS = {
  STANDARD: 'standard',
  OVERVIEW: 'overview',
};

export const DATA_TYPE_TIME_UNITS = {
  SECONDS: 'SECONDS',
  MILLISECONDS: 'MILLISECONDS',
};

export const DASHBOARD_COMPETITOR_ROUTE_INFO = {
  ADD_COMPETITOR: {
    routeName: 'settings.competitors',
    buttonTitle: 'Add Competitor',
  },
  NEW_COMPETITOR: {
    routeName: 'settings.competitors',
    buttonTitle: 'Add a new competitor',
  },
};

export const DASHBOARD_ROUTE_NAMES = {
  DASHBOARDS: 'dashboards',
  ID: 'dashboards.id',
  OVERVIEW: 'dashboards.overview',
  REPORT: 'dashboards.report',
  KAWO: 'dashboards.kawo',
  SHARED: 'dashboards.shared',
};

export const IMAGE_DOWNLOAD_FILE_FORMATS = {
  PNG: {
    extension: 'png',
    dataUrl: 'image/png',
  },
  JPEG: {
    extension: 'jpeg',
    dataUrl: 'image/jpeg',
  },
};

export const CAMPAIGN_STATUS_OPTIONS = Object.freeze({
  [CHANNELS.META_ADS.value]: [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Archive', value: 'ARCHIVED' },
    { label: 'Paused', value: 'PAUSED' },
    { label: 'With Issues', value: 'WITH_ISSUES' },
  ],
  [CHANNELS.TIKTOK_ADS.value]: [
    { label: 'Active', value: 'ENABLE' },
    { label: 'Deleted', value: 'DELETED' },
    { label: 'Paused', value: 'DISABLE' },
    { label: 'Over Budget', value: 'BUDGET_EXCEED' },
  ],
});

export const CONTENT_REPORT_MEDIA_LIST_SIZES = {
  VISION_AI_THUMBNAIL_MODE: 2,
  VISION_AI_EXPANDED_MODE: 4,
  DASHBOARDS: 10,
};

const DEFAULT_DATE_RANGE_OPTION = Object.freeze({
  CUSTOM: {
    value: 'CUSTOM',
    label: 'CUSTOM',
  },
});

export const CONTENT_TAG_MATCH_TYPES = Object.freeze({
  ALL: 'ALL',
  ANY: 'ANY',
});

export const MESSAGE_TAG_MATCH_TYPES = Object.freeze({
  ANY: 'ANY',
  ALL: 'ALL',
});

export const MESSAGE_TAG_MATCH_TYPE_OPTIONS = Object.freeze({
  [MESSAGE_TAG_MATCH_TYPES.ANY]: {
    label: 'Match Any',
    value: MESSAGE_TAG_MATCH_TYPES.ANY,
  },
  [MESSAGE_TAG_MATCH_TYPES.ALL]: {
    label: 'Match All',
    value: MESSAGE_TAG_MATCH_TYPES.ALL,
  },
});

const GRAPH_SCALE_FILTER_DETAILS = {
  label: 'View Graph',
  filter: 'graphScale',
  options: [
    {
      value: GRAPH_SCALES.DAILY.value,
      label: GRAPH_SCALES.DAILY.label,
    },
    {
      value: GRAPH_SCALES.MONTHLY.value,
      label: GRAPH_SCALES.MONTHLY.label,
    },
  ],
};

const REPORTING_PERIOD_FILTERS = Object.freeze({
  LAST_24_HOURS: {
    value: 'LAST_24_HOURS',
    text: 'last 24 hours',
  },
  LAST_7_DAYS: {
    value: 'LAST_7_DAYS',
    text: 'Last 7 Days',
  },
  LAST_WEEK: {
    value: 'LAST_WEEK',
    text: 'Last Week',
  },
  LAST_4_WEEKS: {
    value: 'LAST_4_WEEKS',
    text: 'Last 4 Weeks',
  },
  LAST_MONTH: {
    value: 'LAST_MONTH',
    text: 'Last Month',
  },
  WEEK_TO_DATE: {
    value: 'WEEK_TO_DATE',
    text: 'Week to Date',
  },
  MONTH_TO_DATE: {
    value: 'MONTH_TO_DATE',
    text: 'Month to Date',
  },
  QUARTER_TO_DATE: {
    value: 'QUARTER_TO_DATE',
    text: 'Quarter to Date',
  },
  YEAR_TO_DATE: {
    value: 'YEAR_TO_DATE',
    text: 'Year to Date',
  },
  CUSTOM: {
    value: 'CUSTOM',
    text: 'Custom',
  },
});

const COMPARISON_PERIOD_FILTERS = Object.freeze({
  PREVIOUS_24_HOURS: {
    value: 'PREVIOUS_24_HOURS',
    text: 'Previous 24 hours',
  },
  PREVIOUS_7_DAYS: {
    value: 'PREVIOUS_7_DAYS',
    text: 'Previous 7 Days',
  },
  PREVIOUS_WEEK: {
    value: 'PREVIOUS_WEEK',
    text: 'Previous Week',
  },
  PREVIOUS_4_WEEKS: {
    value: 'PREVIOUS_4_WEEKS',
    text: 'Previous 4 Weeks',
  },
  PREVIOUS_MONTH: {
    value: 'PREVIOUS_MONTH',
    text: 'Previous Month',
  },
  PREVIOUS_WEEK_TO_DATE: {
    value: 'PREVIOUS_WEEK_TO_DATE',
    text: 'Previous Week to Date',
  },
  PREVIOUS_MONTH_TO_DATE: {
    value: 'PREVIOUS_MONTH_TO_DATE',
    text: 'Previous Month to Date',
  },
  PREVIOUS_QUARTER_TO_DATE: {
    value: 'PREVIOUS_QUARTER_TO_DATE',
    text: 'Previous Quarter to Date',
  },
  YEAR_BEFORE: {
    value: 'YEAR_BEFORE',
    text: 'Year Before',
  },
  CUSTOM: {
    value: 'CUSTOM',
    text: 'Custom',
  },
});

const TO_DATE_REPORTING_PRESETS = [
  {
    value: 'WEEK_TO_DATE',
    label: 'Week to Date',
    matchingPreset: 'PREVIOUS_WEEK_TO_DATE',
    range: () => {
      const end = dayjs().subtract(1, 'day');
      return {
        start: end.startOf('week').toDate(),
        end: end.endOf('day').toDate(),
      };
    },
  },
  {
    value: 'MONTH_TO_DATE',
    label: 'Month to Date',
    matchingPreset: 'PREVIOUS_MONTH_TO_DATE',
    range: () => {
      const end = dayjs().subtract(1, 'day');
      return {
        start: end.startOf('month').toDate(),
        end: end.endOf('day').toDate(),
      };
    },
  },
  {
    value: 'QUARTER_TO_DATE',
    label: 'Quarter to Date',
    matchingPreset: 'PREVIOUS_QUARTER_TO_DATE',
    range: () => {
      const end = dayjs().subtract(1, 'day');
      return {
        start: end.startOf('quarter').toDate(),
        end: end.endOf('day').toDate(),
      };
    },
  },
  {
    value: 'YEAR_TO_DATE',
    label: 'Year to Date',
    matchingPreset: 'YEAR_BEFORE',
    range: () => {
      const end = dayjs().subtract(1, 'day');
      return {
        start: end.startOf('year').toDate(),
        end: end.endOf('day').toDate(),
      };
    },
  },
];

const TO_DATE_COMPARISON_PRESETS = [
  {
    value: 'PREVIOUS_WEEK_TO_DATE',
    label: 'Previous Week to Date',
    matchingPreset: 'WEEK_TO_DATE',
    range: ({ targetRange }: { targetRange?: { start?: Date; end?: Date } }) => {
      if (!targetRange?.start || !targetRange?.end) return undefined;
      return {
        start: dayjs(targetRange.start).subtract(1, 'week').startOf('day').toDate(),
        end: dayjs(targetRange.end).subtract(1, 'week').endOf('day').toDate(),
      };
    },
  },
  {
    value: 'PREVIOUS_MONTH_TO_DATE',
    label: 'Previous Month to Date',
    matchingPreset: 'MONTH_TO_DATE',
    range: ({ targetRange }: { targetRange?: { start?: Date; end?: Date } }) => {
      if (!targetRange?.start || !targetRange?.end) return undefined;
      return {
        start: dayjs(targetRange.start).subtract(1, 'month').startOf('day').toDate(),
        end: dayjs(targetRange.end).subtract(1, 'month').endOf('day').toDate(),
      };
    },
  },
  {
    value: 'PREVIOUS_QUARTER_TO_DATE',
    label: 'Previous Quarter to Date',
    matchingPreset: 'QUARTER_TO_DATE',
    range: ({ targetRange }: { targetRange?: { start?: Date; end?: Date } }) => {
      if (!targetRange?.start || !targetRange?.end) return undefined;
      return {
        start: dayjs(targetRange.start).subtract(3, 'month').startOf('day').toDate(),
        end: dayjs(targetRange.end).subtract(3, 'month').endOf('day').toDate(),
      };
    },
  },
];

export const DASHBOARD_REPORTING_PRESETS = [
  ...DATE_RANGE_PRESETS.filter((p) => p.value !== 'ALL_TIME'),
  ...TO_DATE_REPORTING_PRESETS,
];

export const DASHBOARD_COMPARISON_PRESETS = [
  ...DATE_RANGE_COMPARISON_PRESETS,
  ...TO_DATE_COMPARISON_PRESETS,
];

export const MAX_COMPETITOR_FILTER_LIMIT_MESSAGE =
  'Displaying the first 100 results. Please narrow your search.';

export const FILTER_TAB_DISABLED_MESSAGE = {
  BRAND_TAG: 'You must clear the select brands to choose brand tags',
  BRAND: 'You must clear the select brand tags to choose brands',
  COMPETITOR_TAG: 'You must clear the select competitors to choose competitor tags',
  COMPETITOR: 'You must clear the select competitor tags to choose competitors',
};

const BRAND_ID_FILTER_FIELD = 'brandIds';
const BRAND_TAG_FILTER_FIELD = 'brandTagIds';
const BRAND_ID_FILTER_SORT = 'brands';
const BRAND_TAG_FILTER_SORT = 'brandTags';
const BRAND_FILTER_DETAILS = Object.freeze({
  label: 'Brands',
  sortOptions: [
    {
      value: BRAND_ID_FILTER_SORT,
      label: 'Brands',
      filter: 'brandIds',
      disabledTooltip: FILTER_TAB_DISABLED_MESSAGE.BRAND,
    },
    {
      value: BRAND_TAG_FILTER_SORT,
      label: 'Brand Tags',
      filter: 'brandTagIds',
      disabledTooltip: FILTER_TAB_DISABLED_MESSAGE.BRAND_TAG,
    },
  ],
});

const COMPETITOR_ID_FILTER_FIELD = 'competitorSourceAccountIds';
const COMPETITOR_TAG_FILTER_FIELD = 'competitorTagIds';
const COMPETITOR_ID_FILTER_SORT = 'competitors';
const COMPETITOR_TAG_FILTER_SORT = 'competitorTags';
const COMPETITOR_FILTER_DETAILS = Object.freeze({
  label: 'Competitor',
  sortOptions: [
    {
      value: COMPETITOR_ID_FILTER_SORT,
      label: 'Competitors',
      filter: 'competitorSourceAccountIds',
      disabledTooltip: FILTER_TAB_DISABLED_MESSAGE.COMPETITOR,
    },
    {
      value: COMPETITOR_TAG_FILTER_SORT,
      label: 'Competitor Tags',
      filter: 'competitorTagIds',
      disabledTooltip: FILTER_TAB_DISABLED_MESSAGE.COMPETITOR_TAG,
    },
  ],
});

export const DASHBOARD_FILTER_DETAILS = Object.freeze({
  DASHBOARD: {
    scope: 'dashboard',
    options: {
      reportingPeriod: {},
      comparisonPeriod: {
        label: 'Comparison Period',
        required: true,
      },
      graphScale: GRAPH_SCALE_FILTER_DETAILS.options,
      brandIds: BRAND_FILTER_DETAILS.sortOptions,
      competitorSourceAccountIds: COMPETITOR_FILTER_DETAILS.sortOptions,
      sectionHeader: {
        title: 'Temporary Report Adjustments',
      },
      sectionBanner: {
        message: 'Temporarily adjusts the dataset in all reports on this Dashboard.',
        ctaButtonText: 'Learn more',
        resourceCenterUrl: getResourceCenterArticleUrl('34954986292109'),
      },
      contentTags: {
        title: 'Content Tags',
        tooltip: 'Filter by content that match any or all applied tags',
      },
      messageTags: {
        title: 'Message Tags',
        bannerMessage: "This filter only applies to 'Activity This Period' community reports",
      },
    },
    default: {
      reportingPeriod: {
        preset: REPORTING_PERIOD_FILTERS.LAST_7_DAYS.value,
      },
      comparisonPeriod: {
        preset: COMPARISON_PERIOD_FILTERS.PREVIOUS_7_DAYS.value,
      },
      graphScale: GRAPH_SCALES.DAILY.value,
      contentTagIds: [],
      contentTagMatchType: CONTENT_TAG_MATCH_TYPES.ANY,
      messageTagIds: [],
      messageTagMatchType: MESSAGE_TAG_MATCH_TYPES.ANY,
      brandIds: [],
      competitorSourceAccountIds: [],
      campaignIds: [],
      boardIds: [],
      authorship: [],
      collaboratorUsernames: [],
    },
  },
});

export const REPORT_CONFIRMATION_DIALOG = {
  DISCARD_CHANGES: {
    header: 'Discard Changes?',
    message: 'Are you sure you want to discard your changes?',
    acceptLabel: 'Discard',
    rejectLabel: 'Cancel',
    acceptSeverity: 'danger',
  },
};

export const CUSTOMIZATION_CONFIRMATION_DIALOG = {
  CANCEL_CUSTOMIZATION: {
    header: 'Discard Changes?',
    message: 'If you leave now, any unsaved changes will be lost',
    acceptLabel: 'Discard',
    rejectLabel: 'Cancel',
    acceptSeverity: 'danger',
  },
  AUTO_SAVE: {
    header: 'Auto-Saved Customization Found',
    message:
      'We found unsaved changes to this Dashboard. Would you like to continue where you left off in customization mode?',
    acceptLabel: 'Resume Customization',
    rejectLabel: 'Discard Changes',
    acceptSeverity: 'primary',
  },
};

export const AUTO_SAVE_DISCARDED_TOAST = {
  message: 'Auto-saved version discarded',
  severity: 'info',
};

export const DASHBOARD_CUSTOM_METRICS_PANEL = {
  LINK_TEXT: 'Manage Your Custom Metrics',
  LINK_TEXT_TOOLTIP:
    "You don't have access to custom metrics. Please contact your brand admin for permission.",
  AGGREGATION: 'Aggregation (Advanced)',
  PLACEHOLDER: 'Select a custom metric',
  HEADER: 'Custom Metrics',
  NO_RESULTS: 'No results',
  AGGREGATION_LABEL: 'Aggregation',
};

export const CUSTOM_METRICS_AGGREGATION_OPTIONS = [
  {
    value: 'SUM',
    label: 'Post-Level Sum',
    tooltip:
      'The calculation is applied at the post level, and the values are summed across all posts in the reporting period.',
  },
  {
    value: 'AVERAGE',
    label: 'Post-Level Average',
    tooltip:
      'Calculation applied to each individual post.\n Summing those values (calculation results)\n\nDivide that total by the number of posts.',
  },
];

const REPORT_FORM_REDIRECT_MESSAGE =
  'Leaving this page will cause you to lose your progress. Are you sure you want to proceed?';

const GO_TO_SETTINGS_DIALOG = {
  header: 'Go To Settings?',
  message: REPORT_FORM_REDIRECT_MESSAGE,
  acceptLabel: 'Continue',
  acceptSeverity: 'danger',
} as const;

export const GO_TO_LISTENING_DIALOG = {
  header: 'Go to Your Topics',
  message: REPORT_FORM_REDIRECT_MESSAGE,
  acceptLabel: 'Continue',
  acceptSeverity: 'danger',
} as const;

export const GO_TO_CREATORS_DIALOG = {
  header: 'Go to Your Creators?',
  message: REPORT_FORM_REDIRECT_MESSAGE,
  acceptLabel: 'Continue',
  acceptSeverity: 'danger',
} as const;

export const SEND_TO_SETTINGS = {
  CUSTOM_METRICS_NAME: 'Settings - CustomMetrics',
  CUSTOM_METRICS_PAGE: 'settings.customMetrics',
  CONTENT_TAGS_NAME: 'Settings - ContentTags',
  CONTENT_TAGS_PAGE: 'settings.contentTags',
  GO_TO_SETTINGS_DIALOG,
};

export const CUSTOM_METRIC_CHANNELS_FOR_CHANNEL_ICONS = {
  INSTAGRAM: 'instagram',
  TIKTOK: 'tiktok',
  FACEBOOK: 'facebook',
  X: 'x',
  TWITTER: 'twitter',
  LINKEDIN: 'linkedin',
  INSTAGRAM_STORIES: 'instagramStories',
  PINTEREST: 'pinterest',
};

const CONTENT_REPORT_SORT_OPTIONS = {
  TOP: 'Top Performing',
  LOW: 'Lowest Performing',
};

const SORT_ORDER = {
  top: 'top',
  low: 'low',
};

const REPORT_WIZARD_STEPS = {
  METRIC_TYPE: { order: 1, name: 'MetricType', label: 'Select Metric Type' },
  BRANDS: { order: 2, name: 'brands', label: 'Select brands to report on' },
  CHANNEL: { order: 3, name: 'channels', label: 'Select social media channels' },
  CONTENT_TAGS: {
    order: 4,
    name: 'content_tags',
    label: 'Do you want to report on all content or tagged content?',
  },
  CONTENT_TYPE: { order: 5, name: 'content_type', label: 'Select content type' },
  METRIC: { order: 6, name: 'metric', label: 'Select a metric to report on' },
  MEDIA_TYPE: { order: 7, name: 'media_type', label: 'Select media type' },
  SORT_ORDER: { order: 8, name: 'sort_order', label: 'How would you like to view your report?' },
};

const WIZARD_TRANSITIONS = {
  FORWARD: 'slide-left',
  BACKWARD: 'slide-right',
};

export const METRIC_TYPE_OPTIONS = {
  PLATFORM_METRICS: 'Platform Metric',
  CUSTOM_METRICS: 'Your Custom Metric',
};

export const CUSTOM_METRIC = 'Custom Metric';

const DASHBOARD_MENU = {
  ADD_REPORT: {
    label: 'Add Report',
    icon: 'presentation-chart',
    value: 'presentation-chart',
  },
  ADD_HEADING: {
    label: 'Add Heading',
    icon: 'type',
    value: 'heading',
    commandName: 'Add',
    placeHolder: 'Enter heading',
    toastMessage: 'Heading added to dashboard',
    maxLength: 100,
  },
  ADD_NOTE: {
    label: 'Add Note',
    placeHolder: 'Enter note',
    toastMessage: 'Note added to dashboard',
    icon: 'note',
    value: 'note',
    maxLength: 248,
  },
};

const TOAST_SEVERITY = {
  success: 'success',
  error: 'error',
  danger: 'danger',
};

const TOAST_MESSAGES = {
  error: 'An error has occurred',
};

const DEFAULT_TOAST_LIFE = 3000;

export const SL_RICH_CONTENT_BANNER = {
  title: 'Understand How People Feel About Your Brand',
  message:
    'Get deeper insights into audience sentiment directly in your Dashboard reports with the Social Listening add-on.',
  ctaButton: {
    label: 'Try Social Listening',
    severity: 'upgrade',
  },
  severity: 'upgrade',
  dismissLabel: 'Dismiss',
  bannerSrc: slUpgrade3,
  dismissible: true,
  show: true,
};

export const SENTIMENT_METRICS_LIST = [
  'COMMENT_SENTIMENT_POSITIVE_RATE',
  'COMMENT_SENTIMENT_NEUTRAL_RATE',
  'COMMENT_SENTIMENT_NEGATIVE_RATE',
];

const WIDGET_REPORT_TYPES = {
  HEADER: {
    value: 'header',
  },
  NOTE: {
    value: 'note',
  },
};

export const DASHBOARDS_SIDE_MENU = {
  TITLE: 'Dashboards',
  OVERVIEW: 'Overview',
  SHARED_WITH_YOU: 'Shared with You',
};

const DEFAULT_DASHBOARD_SECTION_TITLE = 'Dashboards';

export const SENTIMENTS = {
  isPositive: 'POSITIVE',
  isNegative: 'NEGATIVE',
  isNeutral: 'NEUTRAL',
};

const LEGACY_ADD_TO_DASHBOARD_BUTTON_TEXT = 'Add to Dashboard';
const ADD_TO_DASHBOARD_BUTTON_TEXT = 'Add Content';

export const REPORT_ERROR_ICONS = {
  ALERT_TRIANGLE: 'alert-triangle',
  TAG: 'tag',
};

export const REPORT_ERROR_MESSAGES = {
  NO_DATA_AVAILABLE: 'No data available for the selected timeframe',
  ACCOUNT_DISCONNECTED: 'Account(s) Disconnected',
  NO_CUSTOM_METRIC_ACCESS: 'No Custom Metric Access',
  NO_METRIC_ACCESS: 'You do not have access to this metric for this brand',
  NO_ACCESS_ERROR_TITLE: "You Don't Have Access",
  SERVER_ERROR_TITLE: 'An error occurred',
  NO_DATA_TITLE: 'No Data Available',
  CUSTOM_METRIC_NOT_FOUND: 'You no longer have access to this custom metric',
  SERVER_ERROR:
    'There was an error retrieving data for this report. ' +
    'Please try again later or contact support if the issue persists.',
  DEFAULT_ERROR_TITLE: 'An error occurred',
  UNABLE_TO_LOAD_MORE_CONTENT: 'Unable to load more content',
  FAILED_TO_LOAD_ADDITIONAL_CONTENT: 'Failed to load additional content',
  NO_BENCHMARK_ACCESS: 'You do not have access to benchmark insights for this brand',
  NO_BRAND_ACCESS: 'You do not have access to this brand',
  ALL_BRANDS_DISCONNECTED: 'All accounts are disconnected',
  NO_ACCESS_TO_TOPICS: 'You do not have access to any topics in this report',
  CROSS_ORG_TOPICS: 'Topics in this report do not belong to the same organization',
  INVALID_TIMESCALE_NON_ADDITIVE_METRIC:
    'This metric cannot be displayed by month. Please set View Graph to Day to see this data.',
  AD_ACCOUNT_DISCONNECTED:
    'This report requires a connected Ad Account. Edit this report to reconnect your ad account.',
  ORGANIC_AND_PROMOTED_AD_ACCOUNT_DISCONNECTED:
    'Your ad account is disconnected. Edit this report to use an organic metric to view available data.',
  AD_ACCOUNT_AND_PLATFORM_DISCONNECTED:
    'The account and ad account for this channel are disconnected. Please connect both to view data.',
};

export const REPORT_ERROR_TOOLTIPS = {
  ACCOUNT_DISCONNECTED:
    'The account for this channel is disconnected. Please connect the account to view data.',
  NO_DASHBOARDS_ACCESS: "This brand doesn't have access to Dashboards",
  NO_METRIC_ACCESS: 'You do not have access to this metric for this brand.',
  NO_CUSTOM_METRIC_ACCESS: 'You do not have access to this custom metric for this brand.',
  NO_COMPETITIVE_ACCESS: 'You do not have access to competitive insights for this brand.',
  CONTENT_TAG_ACCESS: (brandName: string, tagsList: string): string =>
    `${brandName} does not have access to the following tags: "${tagsList}"`,
  NO_ADS_ACCESS: 'You do not have access to ads data for this brand.',
  NO_BENCHMARK_ACCESS: 'You do not have access to industry benchmarks for this brand.',
  NO_COMMUNITY_ACCESS: 'Community data is not available for this brand.',
  NO_COMPETITOR_ACCESS: 'Brands do not have access to this competitor',
  NO_BRAND_ACCESS: 'You do not have access to this brand',
  NO_CREATOR_ACCESS: 'You do not have access to the brand associated with this creator',
  DISCONNECTED_CREATOR_STATUS:
    'This is not a connected creator. Their data is not included in this report.',
  UNAVAILABLE_ASSIGNEE: 'The selected assignee is no longer active or has been removed',
  AD_ACCOUNT_DISCONNECTED:
    'The ad account for this channel is disconnected. Please connect an ad account to view data.',
  ORGANIC_AND_PROMOTED_AD_ACCOUNT_DISCONNECTED:
    'Your ad account is disconnected. Switch to an organic metric to view available data.',
  AD_ACCOUNT_PARTIALLY_CONNECTED:
    'This metric requires an Ad account connection. Your Meta Ads account is only partially connected. Go to Settings > Account Management > Platform Connections > Meta Ads to complete the connection, or change the metric to Organic + Promoted. Otherwise, contact Customer Experience for assistance.',
};

export const REPORT_ERROR_VARIANTS = {
  SECTION_ERROR: 'sectionError',
  SECTION_EMPTY: 'sectionEmpty',
  DEFAULT: 'sectionError',
};

export const ACCOUNT_LEVEL_REPORT_SERVER_ERROR_TYPES = {
  COMPETITOR_NOT_REPORTABLE: 'competitor_not_reportable',
  MISSING_AD_ACCOUNT: 'no_ad_account_matching_report_channels',
  MISSING_AD_ACCOUNT_CONNECTION: 'ad_account_not_connected_for_report_channels',
};
export const FETCH_REPORT_SERVER_ERROR_TYPES = {
  EXTERNAL_DASH_API: 'external_api_error',
  NO_ACCESS_TO_TOPICS: 'no_access_to_topics',
  CROSS_ORG_TOPICS: 'cross_org_topics',
  INVALID_TIMESCALE_NON_ADDITIVE_METRIC: 'invalid_time_scale_non_additive_metric',
  MISSING_AD_ACCOUNT: 'no_valid_ad_accounts',
  REPORT_TOO_LARGE: 'report_too_large_error',
};

export const TIME_PERIOD_MESSAGES = {
  UGC_TIME_PERIOD_EMPTY_STATE_MESSAGE:
    'Top UGC may be unavailable for dates prior to September 2019',
  CONTENT_REPORT_EMPTY_STATE_MESSAGE: 'No posts found during the selected time frame',
  METRIC_NOT_AVAILABLE_BEFORE_DATE: (formattedDate: string): string =>
    `This metric is not available for dates before ${formattedDate}`,
  METRIC_NOT_AVAILABLE_AFTER_DATE: (formattedDate: string): string =>
    `This metric is not available for dates after ${formattedDate}`,
};

export const CONTENT_REPORT_PANEL = {
  DEFAULT_REPORT_SORT_ORDER: 'DESC',
  DEFAULT_REPORT_VARIANT: 'section',
  DEFAULT_POST_TYPE: 'organic',
};

export const CONTENT_REPORT_CARD_COLLABORATOR_TOOLTIP =
  'Brands tagged as collaborators on this post.';

export const SORT_ORDER_OPTIONS = Object.freeze([
  {
    value: 'DESC',
    text: 'Top Performing Posts',
    ugcText: 'UGC Top Performing Posts',
    storiesText: 'Top Performing Stories',
  },
  {
    value: 'ASC',
    text: 'Lowest Performing Posts',
    storiesText: 'Lowest Performing Stories',
  },
]);

export const POST_THIS_PERIOD_FILTER_BANNER_MESSAGE =
  'This filter only applies to "Posts This Period" reports';

export const DASHBOARD_TEMPLATE_KEYS = [
  'templateBrandIds',
  'templateBrandNames',
  'templateCompetitorIds',
  'templateCompetitorNames',
  'templateContentTagIds',
  'templateContentTagNames',
  'templateMessageTagIds',
  'templateMessageTagNames',
  'templateCreatorIds',
  'templateCreatorNames',
  'templateTopicIds',
  'templateTopicNames',
  'templateComparativeTopicIds',
  'templateComparativeTopicNames',
  'templateChannels',
];

export const ONLINE_FOLLOWER_HOUR_BUCKET_SIZE = 3;

export default {
  ADD_TO_DASHBOARD_BUTTON_TEXT,
  LEGACY_ADD_TO_DASHBOARD_BUTTON_TEXT,
  COMPARISON_PERIOD_FILTERS,
  CONTENT_REPORT_SORT_OPTIONS,
  CONTENT_TYPE_OPTIONS,
  CONTEXT_DATE_RANGE_LS_KEY,
  CONTEXT_PERIOD_MODE_LS_KEY,
  CUSTOM_METRIC,
  CUSTOM_METRICS_AGGREGATION_OPTIONS,
  DASHBOARD_FILTER_DETAILS,
  DASHBOARD_ROW_HEIGHT_IN_PIXELS,
  DASHBOARD_TOOLTIPS,
  DASHBOARDS_SIDE_MENU,
  DATA_TYPE_TIME_UNITS,
  DEFAULT_DASHBOARD_SECTION_TITLE,
  DEFAULT_DATE_RANGE_OPTION,
  DEFAULT_TOAST_LIFE,
  FORMAT_STRING,
  GRAPH_SCALE_FILTER_DETAILS,
  GRAPH_SCALE_LOCAL_STORE_KEY,
  CONTENT_TAG_IDS_LOCAL_STORE_KEY,
  CONTENT_TAG_MATCH_TYPE_LOCAL_STORE_KEY,
  MESSAGE_TAG_IDS_LOCAL_STORE_KEY,
  MESSAGE_TAG_MATCH_TYPE_LOCAL_STORE_KEY,
  BRAND_IDS_LOCAL_STORE_KEY,
  BRAND_TAG_IDS_LOCAL_STORE_KEY,
  COMPETITOR_IDS_LOCAL_STORE_KEY,
  COMPETITOR_TAG_IDS_LOCAL_STORE_KEY,
  CAMPAIGN_IDS_LOCAL_STORE_KEY,
  BOARD_IDS_LOCAL_STORE_KEY,
  AUTHORSHIP_LOCAL_STORE_KEY,
  COLLABORATOR_USERNAMES_LOCAL_STORE_KEY,
  LAYOUT_LOCAL_STORE_KEY,
  IG_CONTENT_OPTIONS,
  IMAGE_DOWNLOAD_FILE_FORMATS,
  MAX_GRAPH_DATASETS,
  MAX_NUMBER_OF_CONTENT_REPORTS,
  METRIC_TYPE_OPTIONS,
  NUMBER_OF_DASHBOARD_COLS,
  POPUP_MESSAGES,
  REPORT_DATE_RANGE_LS_KEY,
  REPORT_WIZARD_STEPS,
  REPORTING_PERIOD_FILTERS,
  REPORTING_PERIOD_MODE_LS_KEY,
  ROLES,
  SORT_ORDER,
  USER_STATUS,
  WIZARD_TRANSITIONS,
  WIDGET_REPORT_TYPES,
  DASHBOARD_MENU,
  REPORT_MENU,
  TOAST_SEVERITY,
  MAX_COMPETITOR_FILTER_LIMIT_MESSAGE,
  BRAND_ID_FILTER_FIELD,
  BRAND_TAG_FILTER_FIELD,
  BRAND_ID_FILTER_SORT,
  BRAND_TAG_FILTER_SORT,
  COMPETITOR_ID_FILTER_FIELD,
  COMPETITOR_TAG_FILTER_FIELD,
  COMPETITOR_ID_FILTER_SORT,
  COMPETITOR_TAG_FILTER_SORT,
  TOAST_MESSAGES,
  POST_THIS_PERIOD_FILTER_BANNER_MESSAGE,
  FILTER_TOOLTIPS,
  ONLINE_FOLLOWER_HOUR_BUCKET_SIZE,
};

export const METRIC_CATEGORIES = {
  LIKESHOP: 'LIKESHOP',
  ACCOUNT: 'ACCOUNT',
} as const;
